import React, { useRef, useEffect, Fragment } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useCommandBar } from 'contexts/command-bar'
import Modal from 'modals/dialog'
import { getUserAgent } from 'utilities/user-agent'
import { useOptionedString } from 'hooks/optioned-string'
import {
    CommandBarContent,
    Container,
    SearchWrapper, Search, DragArea,
    Shortcuts, ShortcutsSection, ShortcutGroup, ShortcutGroupClickable, ShortcutLabel, Toast,
    ResultsWrapper, SectionHeader, SectionHeading, Results, Empty
} from './s'
import Form from 'components/form/controller'
import { Plain } from 'components/button'
import ActiveIcon from './active-icon'
import { Move as Drag } from 'styled-icons/feather'
import { Command, Entity, Announcement } from './result'
import Shortcut from 'components/tiptap/help/shortcut'
import ActionsPopover from './actions'
import { AnimatePresence } from 'framer-motion'
import { GitBranch as API } from 'styled-icons/feather'

const CommandBarModal = () => {
    const { formatMessage } = useIntl()

    const { isMobile } = getUserAgent()

    const resultsRef = useRef()
    const actionsButtonRef = useRef()

    const {
        inputRef, containerRef,

        // Modal
        show, setShow,

        // Drag & Snap
        onMouseDown,
        containerPosition,

        // Search
        query, isQueryValid,
        isSearching,
        handleSearch,

        // Commands
        navigationItems, combinedItems,
        execute, onKeyDown, activeIndex, activeItem,
        toast,

        // Actions
        isShowingActions, setIsShowingActions,
        actionableActionsItems
    } = useCommandBar()

    useEffect(() => {
        // Scroll active item into view
        if(resultsRef.current) {
            const activeItem = resultsRef.current.querySelector('.active')

            if(activeIndex === 0) {
                resultsRef.current.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                })

                return
            }

            if(activeItem) {
                activeItem.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth'
                })
            }
        }
    }, [activeIndex])

    const optionizeString = useOptionedString()

    const isDev = process.env.NODE_ENV === 'development'

    return (
        <Modal
            show={show}
            dismiss={() => setShow(false)}
            dismissOnOutsideClick
            className="fixed plain no-translate prevent-scroll"
            duration={[0, 0]}
            contentWrapper={CommandBarContent}>
            <Container
                {...(isSearching ? { className: 'loading' } : null)}
                style={{ transform: `translate(${containerPosition.x}px, ${containerPosition.y}px)` }}
                ref={containerRef}>
                <Form>
                    <SearchWrapper {...((!!combinedItems.length || isDev) ? { className: 'active' } : null)}>
                        <ActiveIcon />
                        <Search
                            value={query}
                            placeholder={formatMessage({
                                id: 'action_what_do_you_want',
                                defaultMessage: 'What do you want to do?'
                            })}
                            onChange={handleSearch}
                            onKeyDown={onKeyDown}
                            ref={inputRef} />
                        <DragArea
                            onMouseDown={onMouseDown}
                            role="button">
                            <Drag size={24} />
                        </DragArea>
                    </SearchWrapper>
                </Form>
                <ResultsWrapper ref={resultsRef}>
                    {Object.keys(navigationItems)
                        .filter(key => !!navigationItems[key].total && !!navigationItems[key].items?.length)
                        .map(key => {
                            const navigationItem = navigationItems[key]
                            const sectionKey = `section:${key}`

                            const heading = {
                                favorites: {
                                    id: 'noun_favorites',
                                    defaultMessage: 'Favorites'
                                },
                                recentActions: {
                                    id: 'command_bar_heading_recent',
                                    defaultMessage: 'Recent actions'
                                },
                                commands: {
                                    id: 'noun_results',
                                    defaultMessage: 'Results'
                                },
                                suggestions: {
                                    id: 'command_bar_heading_suggestions',
                                    defaultMessage: 'With ”{query}”…',
                                    values: { query }
                                }
                            }[key]

                            return (
                                <Fragment key={sectionKey}>
                                    {!!heading && (
                                        <SectionHeader>
                                            <SectionHeading>
                                                <FormattedMessage {...heading} />
                                            </SectionHeading>
                                            {navigationItem.reset && (
                                                <Plain
                                                    onClick={navigationItem.reset}
                                                    className="constructive small">
                                                    <FormattedMessage
                                                        id="action_clear"
                                                        defaultMessage="Clear" />
                                                </Plain>
                                            )}
                                        </SectionHeader>
                                    )}
                                    <Results>
                                        {navigationItem.items.map(item => {
                                            const {
                                                actionId,
                                                type
                                            } = item

                                            const index = combinedItems.findIndex(({ actionId: id }) => id === actionId)
                                            const isActive = activeIndex === index

                                            const props = {
                                                item,
                                                isActive
                                            }

                                            const Item = {
                                                announcement: Announcement,
                                                entity: Entity
                                            }[type] ?? Command

                                            return (
                                                <Item
                                                    {...props}
                                                    key={`${sectionKey}:command:${type}:${actionId}`} />
                                            )
                                        })}
                                    </Results>
                                </Fragment>
                            )
                        })
                    }
                    {(isQueryValid && !combinedItems.length && !isSearching) && (
                        <Results>
                            <Empty>
                                <FormattedMessage
                                    id="command_bar_empty"
                                    defaultMessage="🤷 Sorry, no results were found." />
                            </Empty>
                        </Results>
                    )}
                    {(isDev && !!global?.huma?.storage?.local && !isQueryValid) && (
                        <>
                            <SectionHeader>
                                <SectionHeading>🤓 Current API</SectionHeading>
                            </SectionHeader>
                            <Results>
                                <Command
                                    item={{
                                        actionId: 'dev:current-api',
                                        icon: API,
                                        label: getAPI()
                                    }}
                                    isActive={false} />
                            </Results>
                        </>
                    )}
                </ResultsWrapper>
                {(!!combinedItems.length && !isMobile) && (
                    <Shortcuts>
                        <AnimatePresence exitBeforeEnter>
                            {toast && (
                                <Toast key="toast">
                                    <span className="message">{toast.message}</span>
                                    {toast?.label && (
                                        <span className="label">{toast.label}</span>
                                    )}
                                </Toast>
                            )}
                            {!toast && (
                                <ShortcutGroup key="navigate">
                                    <Shortcut
                                        shortcut={['arrowup']}
                                        salt="command-bar:shortcuts" />
                                    <Shortcut
                                        shortcut={['arrowdown']}
                                        salt="command-bar:shortcuts" />
                                    <ShortcutLabel>
                                        <FormattedMessage
                                            id="action_navigate"
                                            defaultMessage="Navigate" />
                                    </ShortcutLabel>
                                </ShortcutGroup>
                            )}
                        </AnimatePresence>
                        <ShortcutsSection>
                            <ShortcutGroupClickable onClick={() => execute(activeItem)}>
                                <Shortcut
                                    shortcut={['enter']}
                                    className="fill"
                                    salt="command-bar:shortcuts:select" />
                                <ShortcutLabel>
                                    <FormattedMessage
                                        id="action_select"
                                        defaultMessage="Select" />
                                </ShortcutLabel>
                            </ShortcutGroupClickable>
                            {(actionableActionsItems.length > 1) && (
                                <ShortcutGroupClickable
                                    onClick={() => setIsShowingActions(isShowingActions => !isShowingActions)}
                                    {...(isShowingActions && { className: 'active' })}
                                    ref={actionsButtonRef}>
                                    <Shortcut
                                        shortcut={[
                                            'mod',
                                            '.'
                                        ]}
                                        className="fill"
                                        salt="command-bar:shortcuts:actions" />
                                    <ShortcutLabel>
                                        {optionizeString(formatMessage({
                                            id: 'noun_more',
                                            defaultMessage: 'More…'
                                        }))}
                                    </ShortcutLabel>
                                </ShortcutGroupClickable>
                            )}
                        </ShortcutsSection>
                    </Shortcuts>
                )}
                <ActionsPopover actionsButtonRef={actionsButtonRef} />
            </Container>
        </Modal>
    )
}

const getAPI = () => {
    const reviewAPI = global.huma.storage.local.get('review-api')

    if(!reviewAPI) {
        return 'dev.api.humahr.com'
    }

    // If reviewAPI is a four-digit number, it's a PR
    if(/^\d{4}$/.test(reviewAPI)) {
        return `huma-api-pr-${reviewAPI}.herokuapp.com`
    }

    return `${reviewAPI}.api.humahr.com`
}

export default CommandBarModal
